import { Injectable } from '@angular/core';
import { BaseService } from '@services/base.service';
import { CustomObject } from '@shared/modules/custom-objects/custom-objects.types';
import { Contact } from 'app/modules/contacts/contacts.types';
import { Entity } from 'app/modules/jupiter/types';
import { LoanQuote } from 'app/modules/projects/details/loans/loans.types';
import { map, Observable } from 'rxjs';
import { Task } from 'app/shared/modules/legacy-tasks/tasks.types';
import { Project, Property } from 'app/modules/projects/projects.types';

@Injectable({ providedIn: 'any' })
export class BaseApiService extends BaseService {
  private tsObject: CustomObject; // this comes only from the jupiter context

  getById<T extends Entity>(id: number): Observable<T> {
    const { api_path: path, api_response_key: responseKey } = this.tsObject.apiResponseConfig;

    return this.httpClient.get(`${path}/${id}`).pipe(map((response) => response[responseKey]));
  }

  update<T extends Entity>(entity: T): Observable<T> {
    const { api_path: path, api_response_key: responseKey } = this.tsObject.apiResponseConfig;

    return this.httpClient
      .patch(`${path}/${entity.id}`, { [responseKey]: entity })
      .pipe(map((response) => response[responseKey]));
  }

  add<T extends Entity>(entity: T): Observable<T> {
    const { api_path: path, api_response_key: responseKey } = this.tsObject.apiResponseConfig;

    return this.httpClient.post(`${path}`, { [responseKey]: entity }).pipe(map((response) => response[responseKey]));
  }

  delete<T extends Entity>(id: number): Observable<T> {
    const { api_path: path, api_response_key: responseKey } = this.tsObject.apiResponseConfig;

    return this.httpClient.delete(`${path}/${id}`).pipe(map((response) => response[responseKey]));
  }

  getRelatedDeals(entityId: number): Observable<Project[]> {
    const { api_path: path } = this.tsObject.apiResponseConfig;

    return this.httpClient
      .get<{
        count: number;
        next: string;
        offset: number;
        previous: string;
        projects: Project[];
      }>(`${path}/${entityId}/projects`, { params: { page: 1, per_page: 250, sort: 'name', order: 'asc' } })
      .pipe(map((response) => response.projects));
  }

  getRelatedProperties(entityId: number): Observable<Property[]> {
    const { api_path: path } = this.tsObject.apiResponseConfig;

    return this.httpClient
      .get<{
        count: number;
        next: string;
        offset: number;
        previous: string;
        properties: Property[];
      }>(`${path}/${entityId}/properties`, { params: { page: 1, per_page: 250, sort: 'name', order: 'asc' } })
      .pipe(map((response) => response.properties));
  }

  getRelatedContacts(entityId: number): Observable<Contact[]> {
    const { api_path: path } = this.tsObject.apiResponseConfig;

    return this.httpClient
      .get<{ contacts: Contact[] }>(`${path}/${entityId}/contacts`)
      .pipe(map(({ contacts }) => contacts));
  }

  getRelatedLoanQuotes(entityId: number): Observable<LoanQuote[]> {
    const { api_path: path } = this.tsObject.apiResponseConfig;

    return this.httpClient
      .get<{ loan_quotes: LoanQuote[] }>(`${path}/${entityId}/loan_quotes`)
      .pipe(map(({ loan_quotes }) => loan_quotes));
  }

  getRelatedTasks(entityId: number): Observable<Task[]> {
    const { api_path: path } = this.tsObject.apiResponseConfig;

    return this.httpClient.get<{ tasks: Task[] }>(`${path}/${entityId}/tasks`).pipe(map(({ tasks }) => tasks));
  }

  addTask(entityId: string, task: Task): Observable<Task> {
    const { api_path: path } = this.tsObject.apiResponseConfig;

    return this.httpClient
      .post<{ task: Task }>(`${path}/${entityId}/task_lists/${task.task_list_id}/tasks`, { task })
      .pipe(map((response) => response.task));
  }
}
